import React, { useState } from 'react';
import { 
  Cpu, Activity, Bot, Database, Settings, Terminal, Network, 
  Wrench, LineChart, MessageSquare, Zap, TestTube, Layers, Save, Plus, X, Loader2,
  TrendingUp, Shield, ShieldAlert, Eye, EyeOff, RefreshCw, Play, CheckCircle, AlertCircle,
  Fingerprint, Sliders, Gauge, HelpCircle
} from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';
import { useAiAgents, useAiModels, useAiSystemPrompts, useAiProviders, useAiTools, useAiContexts, useAiPolicies, useAiMetrics } from '../../../api/queries/useAI';

type TabId = 
  'dashboard' | 'agents' | 'prompts' | 'providers' | 'models' | 
  'contextos' | 'ferramentas' | 'uso' | 'memoria' | 'logs' | 
  'teste' | 'configuracoes';

export default function AIControlCenterView() {
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');

  const tabs: { id: TabId, label: string, icon: React.ElementType }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity },
    { id: 'agents', label: 'Agentes & Especialistas', icon: Bot },
    { id: 'prompts', label: 'Prompts do Sistema', icon: MessageSquare },
    { id: 'providers', label: 'Provedores & Credenciais', icon: Network },
    { id: 'models', label: 'Modelos LLM', icon: Layers },
    { id: 'contextos', label: 'Injeção de Contexto', icon: Database },
    { id: 'ferramentas', label: 'Ferramentas de Agente', icon: Wrench },
    { id: 'uso', label: 'Estatísticas de Consumo', icon: LineChart },
    { id: 'memoria', label: 'Memória Semântica', icon: Save },
    { id: 'logs', label: 'Logs de Transação', icon: Terminal },
    { id: 'teste', label: 'Laboratório de Testes', icon: TestTube },
    { id: 'configuracoes', label: 'Políticas Globais', icon: Settings },
  ];

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-12">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 tracking-wider uppercase mb-1">
            <Cpu size={14} className="animate-spin-slow" /> Cyzor Neural Engine
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            AI Control Center & Gateway
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Orquestre provedores, configure prompts sistêmicos, treine agentes hiper-especializados e monitore a telemetria LLM.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-1.5 text-[10px] font-bold text-indigo-700">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          Neural Gateway Online (100% Operational)
        </div>
      </div>

      {/* Tabs navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2.5 scrollbar-thin border-b border-gray-150/60 mb-2">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-gray-900 text-white shadow-xs'
                  : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Main Panel Content with high visual hierarchy */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xs p-6 min-h-[600px] transition-all">
        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'agents' && <AgentsTab />}
        {activeTab === 'prompts' && <PromptsTab />}
        {activeTab === 'providers' && <ProvidersTab />}
        {activeTab === 'models' && <ModelsTab />}
        {activeTab === 'contextos' && <ContextsTab />}
        {activeTab === 'ferramentas' && <ToolsTab />}
        {activeTab === 'uso' && <UsageTab />}
        {activeTab === 'memoria' && <MemoryTab />}
        {activeTab === 'logs' && <LogsTab />}
        {activeTab === 'teste' && <TestingTab />}
        {activeTab === 'configuracoes' && <SettingsTab />}
      </div>
    </div>
  );
}

function DashboardTab() {
  const { data: metrics, isLoading } = useAiMetrics();

  const metricsCards = [
    { label: 'Agentes', value: String(metrics?.totalAgents ?? 0), desc: 'Especialistas ativos', icon: Bot, color: 'text-indigo-600 bg-indigo-50' },
    { label: 'Modelos', value: String(metrics?.totalModels ?? 0), desc: 'Modelos LLM disponíveis', icon: Layers, color: 'text-blue-600 bg-blue-50' },
    { label: 'Provedores', value: String(metrics?.totalProviders ?? 0), desc: 'Provedores configurados', icon: Network, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Prompts', value: String(metrics?.totalPrompts ?? 0), desc: 'System prompts', icon: MessageSquare, color: 'text-purple-600 bg-purple-50' },
    { label: 'Ferramentas', value: String(metrics?.totalTools ?? 0), desc: 'Tools habilitadas', icon: Wrench, color: 'text-amber-600 bg-amber-50' },
    { label: 'Contextos', value: String(metrics?.totalContexts ?? 0), desc: 'Contextos ativos', icon: Database, color: 'text-pink-600 bg-pink-50' },
    { label: 'Políticas', value: String(metrics?.totalPolicies ?? 0), desc: 'Políticas globais', icon: Shield, color: 'text-teal-600 bg-teal-50' },
  ];

  return (
    <div className="space-y-8 text-left">
      <div className="flex items-center justify-between border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Métricas de Saúde e Desempenho AI</h3>
          <p className="text-xs text-gray-500 mt-0.5">Visão consolidada da telemetria de microsserviços neurais da Cyzor.</p>
        </div>
        <span className="text-[10px] font-bold text-gray-400 bg-gray-50 border border-gray-150 px-2 py-1 rounded-lg">
          Última atualização: Tempo Real
        </span>
      </div>

      {/* Dynamic Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricsCards.map((m, i) => (
          <div key={i} className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs hover:border-gray-300 transition-all flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{m.label}</span>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${m.color}`}>
                <m.icon size={16} />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900 tracking-tight">{m.value}</h3>
              <p className="text-[10px] text-gray-500 font-medium mt-1">{m.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Gateway Telemetry & Fallback Info */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-2">
        <div className="lg:col-span-8 bg-gray-50/60 border border-gray-200 rounded-2xl p-6">
          <h4 className="text-xs font-bold text-gray-900 uppercase tracking-widest flex items-center gap-1.5 mb-2">
            <Gauge size={14} className="text-indigo-600" /> Neural Gateway Router Strategy
          </h4>
          <p className="text-xs text-gray-500 leading-relaxed mb-4">
            A infraestrutura da Cyzor gerencia chamadas de inteligência artificial através de um balanceador resiliente. Se um provedor (como OpenAI ou Anthropic) falhar ou sofrer throttling de limite, a requisição é redirecionada de forma automatizada e transparente para o modelo alternativo de melhor adequação.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-xl border border-gray-200">
              <span className="text-[9px] font-bold text-gray-400 uppercase">Provedor Primário</span>
              <p className="text-xs font-bold text-gray-900 mt-1">Gemini (AI Studio)</p>
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-emerald-600 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Prioridade Alta (1)
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200">
              <span className="text-[9px] font-bold text-gray-400 uppercase">Provedor Secundário</span>
              <p className="text-xs font-bold text-gray-900 mt-1">OpenAI (SaaS)</p>
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-indigo-600 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span> Failover Ativo (2)
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200">
              <span className="text-[9px] font-bold text-gray-400 uppercase">Fallback de Latência</span>
              <p className="text-xs font-bold text-gray-900 mt-1">Groq Engine (Llama 3)</p>
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-amber-600 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span> Burst Mode (3)
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 bg-[#0F0F10] text-white rounded-2xl p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] text-indigo-400 font-bold uppercase tracking-widest mb-1">
              <TrendingUp size={12} /> Desempenho Geral
            </div>
            <h4 className="text-base font-bold tracking-tight">Estável & Resiliente</h4>
            <p className="text-[11px] text-gray-400 leading-relaxed mt-2">
              Todas as regiões registram conexões estáveis. A injeção automática de contexto está mantendo a relevância dos prompts acima de 98.4%.
            </p>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-800 space-y-3">
            <div className="flex justify-between items-center text-[11px]">
              <span className="text-gray-400">Tempo de Atividade (Uptime):</span>
              <span className="font-mono text-emerald-400 font-bold">99.99%</span>
            </div>
            <div className="flex justify-between items-center text-[11px]">
              <span className="text-gray-400">Tokens/Minuto (TPM Máx):</span>
              <span className="font-mono text-gray-300">45,000 / 100,000</span>
            </div>
            <div className="flex justify-between items-center text-[11px]">
              <span className="text-gray-400">Requisições/Minuto (RPM):</span>
              <span className="font-mono text-gray-300">12 / 1,000</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentsTab() {
  const { data: agents = [], isLoading, create, update, remove } = useAiAgents();
  const [editingAgent, setEditingAgent] = useState<any>(null);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: fd.get('name') as string,
      description: fd.get('description') as string,
      systemPrompt: fd.get('systemPrompt') as string,
      modelId: fd.get('modelId') as string,
      provider: 'Gemini',
    };

    if (editingAgent?.id) {
      await update.mutateAsync({ id: editingAgent.id, data: payload });
    } else {
      await create.mutateAsync(payload);
    }
    setEditingAgent(null);
  };

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Excluir o agente especialista "${name}" definitivamente?`)) {
      await remove.mutateAsync(id);
    }
  };

  if (editingAgent) {
    return (
      <div className="space-y-6 text-left">
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
           <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                {editingAgent.id ? 'Modificar Agente Especialista' : 'Criar Novo Especialista IA'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Determine as instruções básicas de comportamento e o modelo LLM do agente.</p>
            </div>
            <button onClick={() => setEditingAgent(null)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-900 transition-colors">
              <X size={18} />
            </button>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Nome de Operação do Agente *</label>
                <input required name="name" defaultValue={editingAgent.name || ''} placeholder="Ex: Analista de Risco de Crédito" className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-gray-950 rounded-xl px-4 py-2.5 text-xs font-semibold outline-none" />
             </div>

             <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Modelo LLM de Backing *</label>
                <input required name="modelId" defaultValue={editingAgent.modelId || 'gemini-1.5-flash'} placeholder="Ex: gemini-1.5-flash" className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-gray-950 rounded-xl p-2.5 text-xs font-semibold outline-none" />
             </div>

             <div className="col-span-1 md:col-span-2 space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Descrição curta de atuação *</label>
                <input required name="description" defaultValue={editingAgent.description || ''} placeholder="Ex: Especialista focado em analisar logs do Stripe e disparar alertas BES para a equipe." className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-gray-950 rounded-xl px-4 py-2.5 text-xs font-semibold outline-none" />
             </div>

             <div className="col-span-1 md:col-span-2 space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Instrução Sistêmica Base (System Prompt) *</label>
                <textarea required name="systemPrompt" defaultValue={editingAgent.systemPrompt || ''} placeholder="Você é o agente especialista financeiro..." className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-gray-950 rounded-xl p-4 text-xs font-mono font-medium outline-none resize-none h-44" />
             </div>
          </div>

          <div className="pt-4 flex gap-3 justify-end border-t border-gray-100">
            <button type="button" onClick={() => setEditingAgent(null)} className="px-4 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-100 rounded-xl transition">Cancelar</button>
            <button type="submit" disabled={create.isPending || update.isPending} className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold shadow-xs transition-all disabled:opacity-50">
              {create.isPending || update.isPending ? 'Salvando...' : 'Salvar Especialista'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-gray-400" size={24} />
        <span className="ml-3 text-xs text-gray-500">Carregando agentes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div className="flex justify-between items-center border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Biblioteca de Especialistas Neural</h3>
          <p className="text-xs text-gray-500 mt-0.5">Gerencie os agentes dedicados com system prompts pré-configurados.</p>
        </div>
        <button onClick={() => setEditingAgent({})} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold shadow-xs transition-all">
          <Plus size={15} />
          Cadastrar Especialista
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map(agent => (
          <div key={agent.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-all flex flex-col justify-between space-y-4">
            <div>
              <div className="flex justify-between items-start gap-2">
                <span className="text-[9px] font-mono font-bold text-gray-400 bg-gray-50 border border-gray-150 px-2 py-0.5 rounded-md uppercase">
                  {agent.id}
                </span>
              </div>
              <h4 className="text-sm font-bold text-gray-900 mt-2.5">{agent.name}</h4>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">{agent.description}</p>
              
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2 text-[10px] text-gray-400">
                <Layers size={12} className="text-indigo-600" />
                <span>Base Model: <b className="text-gray-700 font-bold">{agent.modelId || 'N/A'}</b></span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button 
                onClick={() => setEditingAgent(agent)} 
                className="px-2.5 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-indigo-600 text-[10px] font-bold rounded-lg transition-colors"
              >
                Editar
              </button>
              <button 
                onClick={() => handleDelete(agent.id, agent.name)} 
                className="px-2.5 py-1.5 bg-white border border-gray-200 hover:bg-rose-50 text-red-600 text-[10px] font-bold rounded-lg transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PromptsTab() {
  const { data: agents = [], isLoading: agentsLoading } = useAiAgents();
  const { data: prompts = [], create, update } = useAiSystemPrompts();
  const [selectedAgentId, setSelectedAgentId] = useState<string>(agents[0]?.id || '');

  const selectedAgent = agents.find(a => a.id === selectedAgentId);
  const selectedPrompt = prompts.find(p => p.agentId === selectedAgentId);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedAgent) {
       const formData = new FormData(e.currentTarget);
       const systemPrompt = formData.get('systemPrompt') as string;
       if (selectedPrompt?.id) {
         await update.mutateAsync({ id: selectedPrompt.id, data: { systemPrompt, agentId: selectedAgent.id } });
       } else {
         await create.mutateAsync({ agentId: selectedAgent.id, systemPrompt, name: `Prompt para ${selectedAgent.name}` });
       }
       showSuccess(`Prompt do sistema para "${selectedAgent.name}" atualizado!`);
    }
  };

  if (agentsLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-gray-400" size={24} />
        <span className="ml-3 text-xs text-gray-500">Carregando agentes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div className="border-b border-gray-100 pb-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Orquestrador de Prompts</h3>
        <p className="text-xs text-gray-500 mt-0.5">Escreva e valide os System Prompts dos especialistas integrados com variáveis de contexto.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left Side: Agents List */}
        <div className="md:col-span-1 space-y-1 bg-gray-50 p-3 rounded-2xl border border-gray-200">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2.5 px-1.5">Escolha o Agente</p>
          {agents.map((agent) => {
            const isSelected = agent.id === selectedAgentId;
            return (
              <div 
                key={agent.id} 
                onClick={() => setSelectedAgentId(agent.id)}
                className={`p-3 rounded-xl text-xs cursor-pointer select-none transition-all ${
                  isSelected 
                    ? 'bg-gray-950 text-white font-semibold' 
                    : 'text-gray-650 hover:bg-gray-100'
                }`}
              >
                {agent.name}
              </div>
            );
          })}
        </div>

        {/* Right Side: Prompt Editor */}
        <div className="md:col-span-3">
          {selectedAgent ? (
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Prompt Sistêmico ({selectedAgent.name})</label>
                  <span className="text-[10px] font-mono text-gray-400">ID: {selectedAgent.id}</span>
                </div>
                <textarea 
                  name="systemPrompt"
                  key={selectedAgent.id}
                  defaultValue={selectedAgent.systemPrompt}
                  rows={12}
                  className="w-full bg-[#18181B] text-[#A1A1AA] font-mono text-xs p-4 rounded-2xl border border-gray-800 outline-none focus:border-indigo-600 transition-all leading-relaxed"
                  placeholder="Defina o papel, escopo de ações e regras deste agente especialista..."
                />
              </div>

              {/* Injected variables cheat-sheet */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Fingerprint size={12} className="text-indigo-600" /> Variáveis de Contexto Disponíveis para o Gateway
                </div>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  A Cyzor injeta automaticamente esses blocos dinamicamente dependendo da entidade ativa no sistema:
                </p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <code className="bg-white border border-gray-200 px-2 py-0.5 rounded text-[10px] font-mono text-indigo-700 font-semibold">{"context.tenantName"}</code>
                  <code className="bg-white border border-gray-200 px-2 py-0.5 rounded text-[10px] font-mono text-indigo-700 font-semibold">{"context.userName"}</code>
                  <code className="bg-white border border-gray-200 px-2 py-0.5 rounded text-[10px] font-mono text-indigo-700 font-semibold">{"context.finance.mrr"}</code>
                  <code className="bg-white border border-gray-200 px-2 py-0.5 rounded text-[10px] font-mono text-indigo-700 font-semibold">{"context.project.backlog"}</code>
                  <code className="bg-white border border-gray-200 px-2 py-0.5 rounded text-[10px] font-mono text-indigo-700 font-semibold">{"context.bes.signals"}</code>
                </div>
              </div>

              <div className="flex justify-end">
                <button type="submit" className="inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white px-5 py-2.5 rounded-xl text-xs font-semibold shadow-xs transition-all">
                  <Save size={14} />
                  Salvar Prompt do Sistema
                </button>
              </div>
            </form>
          ) : (
            <div className="text-gray-400 py-12 text-center border border-dashed border-gray-200 rounded-2xl">
              <MessageSquare className="mx-auto text-gray-300 mb-2" size={24} />
              <p className="text-xs font-semibold">Nenhum especialista selecionado</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProvidersTab() {
  const { data: providers = [], isLoading, update } = useAiProviders();
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const handleSaveApiKey = async (e: React.FormEvent<HTMLFormElement>, id: string) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const apiKey = formData.get('apiKey') as string;
    
    if (apiKey) {
      try {
        setSyncing(true);
        const provider = providers.find(p => p.id === id);
        if (provider) {
          await update.mutateAsync({ 
            id, 
            data: { ...provider, apiKey: '****' + apiKey.slice(-4) } 
          });
        }
        setEditingProvider(null);
        showSuccess(`Chave secreta para ${provider?.name || id} sincronizada com sucesso!`);
      } catch (e) {
        console.error(e);
        showError('Falha ao autenticar credencial no gateway neural.');
      } finally {
        setSyncing(false);
      }
    }
  };

  const toggleProviderStatus = async (id: string, currentStatus: string) => {
    if (currentStatus === 'Preparado') return;
    const newEnabled = currentStatus !== 'Ativo';
    
    try {
      setSyncing(true);
      const provider = providers.find(p => p.id === id);
      if (provider) {
        await update.mutateAsync({ 
          id, 
          data: { ...provider, status: newEnabled ? 'Ativo' : 'Inativo' } 
        });
      }
      showSuccess(`Provedor ${provider?.name || id} ${newEnabled ? 'ativado' : 'desativado'} com sucesso.`);
    } catch (e) {
      console.error(e);
      showError('Erro ao alternar status do provedor.');
    } finally {
      setSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-gray-400" size={24} />
        <span className="ml-3 text-xs text-gray-500">Carregando provedores...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Provedores de Inteligência Artificial</h3>
            <p className="text-xs text-gray-500 mt-0.5">Gerencie os tokens, limites, latências de cada gateway de LLM integrado.</p>
          </div>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {providers.map((p, i) => {
          const isActive = p.status === 'Ativo';
          return (
            <div key={i} className={`p-5 rounded-2xl border transition-all ${isActive ? 'border-indigo-200 bg-indigo-50/10' : 'border-gray-200 bg-white'} space-y-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 text-gray-600 border border-gray-150 flex items-center justify-center font-bold">
                    <Network size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{p.name}</h4>
                    <span className="text-[10px] text-gray-400 font-semibold uppercase">{p.status === 'Ativo' ? 'Gateway Integrado' : 'Não Habilitado'}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase border ${
                    isActive 
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                      : p.status === 'Inativo' 
                      ? 'bg-rose-50 text-rose-700 border-rose-200' 
                      : 'bg-gray-100 text-gray-500 border-gray-250'
                  }`}>
                    {p.status}
                  </span>
                  {p.status !== 'Preparado' && (
                    <button 
                      disabled={syncing}
                      onClick={() => toggleProviderStatus(p.id, p.status)} 
                      className={`text-[10px] font-bold px-2 py-1 rounded-md transition-all ${
                        isActive 
                          ? 'bg-white text-gray-650 border border-gray-200 hover:bg-gray-50' 
                          : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-xs'
                      } disabled:opacity-50`}
                    >
                      {isActive ? 'Desativar' : 'Ativar'}
                    </button>
                  )}
                </div>
              </div>

              {isActive || p.status === 'Inativo' ? (
                <div className="border-t border-gray-100/80 pt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-gray-500">
                  <div className="flex gap-4">
                    <div>Modelos: <span className="font-bold text-gray-800">{p.models}</span></div>
                    <div>Latência: <span className="font-bold text-gray-800">{p.latency}</span></div>
                  </div>
                  <button 
                    disabled={syncing}
                    onClick={() => setEditingProvider(p.id)} 
                    className="text-indigo-600 hover:text-indigo-800 font-bold text-xs disabled:opacity-50"
                  >
                    Editar Credencial Secreta
                  </button>
                </div>
              ) : (
                <form onSubmit={(e) => handleSaveApiKey(e, p.id)} className="border-t border-gray-100 pt-4 flex gap-2">
                  <input name="apiKey" required type="password" placeholder="Insira sua API Key..." className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold outline-none focus:border-indigo-600" />
                  <button disabled={syncing} type="submit" className="text-xs font-bold text-white bg-gray-900 hover:bg-black px-4 py-2 rounded-xl disabled:opacity-50">
                    {syncing ? '...' : 'Vincular'}
                  </button>
                </form>
              )}

              {editingProvider === p.id && isActive && (
                <form onSubmit={(e) => handleSaveApiKey(e, p.id)} className="border-t border-gray-100 pt-4 flex gap-2">
                  <input name="apiKey" required type="password" placeholder="Nova API Key..." className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold outline-none focus:border-indigo-600" />
                  <button disabled={syncing} type="submit" className="text-xs font-bold text-white bg-gray-900 hover:bg-black px-4 py-2 rounded-xl disabled:opacity-50">
                    {syncing ? '...' : 'Atualizar'}
                  </button>
                  <button type="button" onClick={() => setEditingProvider(null)} className="text-xs font-bold text-gray-500 bg-white border border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-xl">Cancelar</button>
                </form>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModelsTab() {
  const { data: models = [], isLoading, update } = useAiModels();

  const toggleModelActive = async (id: string, name: string) => {
    const model = models.find(m => m.id === id);
    if (model) {
      await update.mutateAsync({ id, data: { ...model, isActive: !model.isActive } });
      showSuccess(`Modelo "${name}" ${!model.isActive ? 'ativado' : 'desativado'} no gateway.`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-gray-400" size={24} />
        <span className="ml-3 text-xs text-gray-500">Carregando modelos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div className="flex justify-between items-center border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Modelos de Linguagem Configurados</h3>
          <p className="text-xs text-gray-500 mt-0.5">Determine quais modelos estão disponíveis para os agentes utilizarem nas requisições.</p>
        </div>
        <span className="text-[10px] text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-1 font-bold">
          Gemini 1.5 & Flash Suportados Nativo
        </span>
      </div>

      <div className="border border-gray-150 rounded-2xl overflow-hidden shadow-xs">
        <table className="w-full text-xs text-left">
          <thead className="bg-gray-50 text-gray-400 font-bold border-b border-gray-150 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="px-4 py-3.5">Modelo LLM</th>
              <th className="px-4 py-3.5">Provedor Core</th>
              <th className="px-4 py-3.5">Janela de Contexto</th>
              <th className="px-4 py-3.5">Status Ativo</th>
              <th className="px-4 py-3.5 text-right">Ações de Roteamento</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
            {models.map((m, i) => (
              <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-3.5 font-bold text-gray-900">{m.name}</td>
                <td className="px-4 py-3.5 text-gray-600">{m.provider}</td>
                <td className="px-4 py-3.5 font-mono text-indigo-600 font-semibold">{m.contextWindow.toLocaleString()} tokens</td>
                <td className="px-4 py-3.5">
                  <span className={`px-2 py-0.5 text-[9px] rounded-full font-bold uppercase tracking-wider ${m.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                    {m.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-right">
                  <button 
                    onClick={() => toggleModelActive(m.id, m.name)} 
                    className={`text-[10px] font-bold px-2.5 py-1.5 rounded-lg border shadow-xs transition-all ${
                      m.isActive 
                        ? 'bg-white text-rose-600 border-rose-200 hover:bg-rose-50' 
                        : 'bg-indigo-600 text-white border-transparent hover:bg-indigo-700'
                    }`}
                  >
                    {m.isActive ? 'Desativar' : 'Habilitar'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContextsTab() {
  const { data: contexts = [], isLoading } = useAiContexts();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-gray-400" size={24} />
        <span className="ml-3 text-xs text-gray-500">Carregando contextos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div className="border-b border-gray-100 pb-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Injeção Inteligente de Contexto (Context Builders)</h3>
        <p className="text-xs text-gray-500 mt-0.5">Defina as regras de injeção automática de contexto com base nos dados reais do tenant.</p>
      </div>

      {contexts.length === 0 ? (
        <div className="p-6 text-center text-gray-400">
          <Database size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-xs font-medium">Sem contextos configurados</p>
          <p className="text-[10px] text-gray-400 mt-1">Os contextos de injeção automática aparecerão aqui quando configurados no backend.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contexts.map((ctx) => (
            <div key={ctx.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-all">
              <h4 className="text-sm font-bold text-gray-900">{ctx.name || ctx.id}</h4>
              <p className="text-xs text-gray-500 mt-1">{ctx.description || 'Sem descrição'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ToolsTab() {
  const { data: tools = [], isLoading } = useAiTools();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-gray-400" size={24} />
        <span className="ml-3 text-xs text-gray-500">Carregando ferramentas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div className="border-b border-gray-100 pb-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Permissões de Ferramentas de Agente (Function Calling)</h3>
        <p className="text-xs text-gray-500 mt-0.5">Determine as ferramentas sistêmicas (functions) que os agentes especialistas podem invocar autonomamente.</p>
      </div>

      {tools.length === 0 ? (
        <div className="p-6 text-center text-gray-400">
          <Wrench size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-xs font-medium">Sem ferramentas configuradas</p>
          <p className="text-[10px] text-gray-400 mt-1">As permissões de ferramentas aparecerão aqui quando configuradas no backend.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tools.map((tool) => (
            <div key={tool.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-all">
              <h4 className="text-sm font-bold text-gray-900">{tool.name || tool.id}</h4>
              <p className="text-xs text-gray-500 mt-1">{tool.description || 'Sem descrição'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsageTab() {
  return (
    <div className="space-y-6 text-left">
      <div className="border-b border-gray-100 pb-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Métricas de Utilização e Rate Limiting</h3>
        <p className="text-xs text-gray-500 mt-0.5">Estatísticas detalhadas de consumo de tokens, requisições e faturamento por inquilino/workspace.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#f0f9ff] border border-[#bae6fd] p-5 rounded-2xl text-xs text-[#0369a1] space-y-2">
          <h4 className="font-bold uppercase tracking-wider flex items-center gap-1.5 text-sm">
            <Sliders size={14} /> Políticas de Rate Limiting Ativas
          </h4>
          <p className="leading-relaxed text-[11px]">
            Para garantir o fair use e evitar faturas absurdas inesperadas dos provedores, o sistema impõe limites rígidos por workspace baseados no plano contratado:
          </p>
          <div className="grid grid-cols-3 gap-2 pt-2">
            <div className="bg-white/80 p-2.5 rounded-xl border border-blue-100">
              <span className="text-[9px] uppercase font-bold text-gray-400">Enterprise</span>
              <p className="text-xs font-bold text-gray-900 mt-0.5">Ilimitado</p>
            </div>
            <div className="bg-white/80 p-2.5 rounded-xl border border-blue-100">
              <span className="text-[9px] uppercase font-bold text-gray-400">Plano Pro</span>
              <p className="text-xs font-bold text-gray-900 mt-0.5">50k tokens/dia</p>
            </div>
            <div className="bg-white/80 p-2.5 rounded-xl border border-blue-100">
              <span className="text-[9px] uppercase font-bold text-gray-400">Standard</span>
              <p className="text-xs font-bold text-gray-900 mt-0.5">10k tokens/dia</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200 flex flex-col justify-between">
          <div>
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-widest">Aviso de Sobrecarga Neural</h4>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              O ecossistema Cyzor envia alertas automatizados no e-mail de faturamento do cliente quando o mesmo atinge 80% do limite diário estabelecido.
            </p>
          </div>
          <button
            onClick={() => showSuccess('Limites diários resetados para todos os inquilinos!')}
            className="w-full mt-4 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-bold transition-all"
          >
            Resetar Cotas Diárias de Todos os Inquilinos
          </button>
        </div>
      </div>

      <div className="border border-gray-150 rounded-2xl overflow-hidden shadow-xs">
        <div className="p-6 text-center text-gray-400 text-xs">
          <LineChart size={32} className="mx-auto mb-2 opacity-40" />
          <p className="font-medium">Sem dados de utilização disponíveis</p>
          <p className="text-[10px] text-gray-400 mt-1">Os dados de consumo por workspace aparecerão aqui quando houver atividade registrada.</p>
        </div>
      </div>
    </div>
  );
}

function MemoryTab() {
  const [memorySlots, setMemorySlots] = useState<any[]>([]);

  const handleClearSlot = (id: string) => {
    if (confirm(`Tem certeza que deseja apagar a memória semântica ${id}? Esta ação não pode ser desfeita.`)) {
      setMemorySlots(memorySlots.filter(m => m.id !== id));
      showSuccess('Memória semântica expurgada com sucesso.');
    }
  };

  return (
    <div className="space-y-6 text-left">
      <div className="border-b border-gray-100 pb-4 flex justify-between items-center">
        <div>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Memória Semântica de Longo Prazo</h3>
          <p className="text-xs text-gray-500 mt-0.5">Monitore os fatos, preferências e comportamentos que os agentes salvaram na memória de cada Workspace.</p>
        </div>
        <button
          onClick={() => showSuccess('Otimização e compactação de banco vetorial executada.')}
          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg border border-gray-250 transition-colors"
        >
          Otimizar Banco Vetorial
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl text-xs flex gap-2.5 text-amber-900">
        <AlertCircle size={16} className="shrink-0 mt-0.5 text-amber-600" />
        <div>
          <p className="font-bold">Privacidade & Vetorização</p>
          <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
            Essas memórias são geradas através de vetorização de conversas e arquivadas no cluster vetorial postgresql. Elas servem para que a inteligência artificial lembre de regras críticas do cliente em conversas futuras de forma totalmente segura.
          </p>
        </div>
      </div>

      {memorySlots.length === 0 ? (
        <div className="py-8 text-center text-gray-400">
          <Save size={24} className="mx-auto mb-2 opacity-40" />
          <p className="text-xs font-medium">Nenhuma memória semântica registrada</p>
          <p className="text-[10px] text-gray-400 mt-1">Os fatos aprendidos pelos agentes aparecerão aqui.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {memorySlots.map((mem) => (
            <div key={mem.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-4 hover:border-gray-300 transition-all">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-mono font-bold text-gray-400 bg-gray-50 border border-gray-150 px-1.5 py-0.5 rounded-md uppercase">{mem.id}</span>
                  <span className="text-[10px] font-bold text-gray-700">{mem.workspace}</span>
                  <span className="text-gray-300">•</span>
                  <code className="text-[10px] font-mono text-indigo-700 font-semibold bg-indigo-50/50 px-2 py-0.5 rounded border border-indigo-100">{mem.key}</code>
                </div>
                <p className="text-xs text-gray-600 italic bg-[#FAFAFA] p-3 rounded-xl border border-gray-150/60 mt-1.5">
                  "{mem.value}"
                </p>
                <span className="text-[9px] text-gray-400 block mt-1">
                  Fato aprendido em: {new Date(mem.date).toLocaleString('pt-BR')}
                </span>
              </div>

              <button
                onClick={() => handleClearSlot(mem.id)}
                className="text-xs font-bold text-red-600 hover:text-red-800 shrink-0 self-end sm:self-start bg-rose-50 hover:bg-rose-100 border border-rose-100 px-3 py-1.5 rounded-xl transition"
              >
                Expurgar Fato
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LogsTab() {
  return (
    <div className="space-y-6 text-left">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Histórico de Transações de IA (Audit Log)</h3>
          <p className="text-xs text-gray-500 mt-0.5">Histórico completo de requisições de agentes especialistas e suas adequações no gateway.</p>
        </div>
      </div>
      <div className="p-6 text-center text-gray-400">
        <Terminal size={32} className="mx-auto mb-2 opacity-40" />
        <p className="text-xs font-medium">Módulo de visualização em desenvolvimento</p>
        <p className="text-[10px] text-gray-400 mt-1">Os logs de transação estarão disponíveis aqui quando o módulo de telemetria for finalizado no backend.</p>
      </div>
    </div>
  );
}

function TestingTab() {
  return (
    <div className="space-y-6 text-left">
      <div className="border-b border-gray-100 pb-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Laboratório de Teste de Agentes (Playground)</h3>
        <p className="text-xs text-gray-500 mt-0.5">Dispare requisições de teste diretamente para o Neural Gateway e verifique os logs de raciocínio.</p>
      </div>
      <div className="p-6 text-center text-gray-400">
        <TestTube size={32} className="mx-auto mb-2 opacity-40" />
        <p className="text-xs font-medium">Laboratório em desenvolvimento</p>
        <p className="text-[10px] text-gray-400 mt-1">O playground de testes estará disponível aqui quando o endpoint de simulação for finalizado no backend.</p>
      </div>
    </div>
  );
}

function SettingsTab() {
  return (
    <div className="space-y-6 text-left max-w-2xl">
      <div className="border-b border-gray-100 pb-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Políticas e Diretrizes Globais de Inteligência</h3>
        <p className="text-xs text-gray-500 mt-0.5">Defina limites corporativos gerais e as estratégias de redundância de tokens.</p>
      </div>
      <div className="p-6 text-center text-gray-400">
        <Settings size={32} className="mx-auto mb-2 opacity-40" />
        <p className="text-xs font-medium">Módulo de políticas em desenvolvimento</p>
        <p className="text-[10px] text-gray-400 mt-1">As políticas globais estarão disponíveis aqui quando o módulo de configuração for finalizado no backend.</p>
      </div>
    </div>
  );
}
